import { useCallback, useEffect, useRef } from "react";

type KeyCombination = {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
};

type ShortcutHandler = (event: KeyboardEvent) => void;

type ShortcutConfig = {
  keyCombination: KeyCombination;
  handler: ShortcutHandler;
  preventDefault?: boolean;
  /*
   * Defines whether the shortcut should be triggered even within the focused input field.
   *
   * For now its used only for the Save changes hotkey
   * in Appointment/Physician pages with the input fields (form data).
   */
  allowInInputs?: boolean;
};

// Map of special keys that might have different representations in different browsers
const KEY_MAP: Record<string, string[]> = {
  arrowleft: ["arrowleft", "left"],
  arrowright: ["arrowright", "right"],
  arrowup: ["arrowup", "up"],
  arrowdown: ["arrowdown", "down"],
  escape: ["escape", "esc"],
  delete: ["delete", "del"],
};

/**
 * Custom hook for handling keyboard shortcuts
 * @param shortcuts Array of shortcut configurations
 */
export const useKeyboardShortcuts = (shortcuts: ShortcutConfig[]) => {
  const shortcutsRef = useRef<ShortcutConfig[]>(shortcuts);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const isInputElement =
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable);

    for (const shortcut of shortcutsRef.current) {
      const {
        keyCombination,
        handler,
        preventDefault = true,
        allowInInputs = false,
      } = shortcut;

      // Skip shortcuts that don't allow input elements if we're in an input element
      if (isInputElement && !allowInInputs) {
        continue;
      }

      const {
        key,
        ctrlKey = false,
        metaKey = false,
        altKey = false,
      } = keyCombination;

      const normalizedEventKey = event.key.toLowerCase();
      const normalizedConfigKey = key.toLowerCase();

      const isKeyMatch =
        normalizedEventKey === normalizedConfigKey ||
        (KEY_MAP[normalizedConfigKey] &&
          KEY_MAP[normalizedConfigKey].includes(normalizedEventKey)) ||
        (KEY_MAP[normalizedEventKey] &&
          KEY_MAP[normalizedEventKey].includes(normalizedConfigKey));

      const isLetterKey = /^[a-z]$/.test(normalizedConfigKey);
      const eventKeyIsLetter =
        isLetterKey &&
        (normalizedEventKey === normalizedConfigKey ||
          event.code.toLowerCase() === `key${normalizedConfigKey}`);

      const shiftKey = keyCombination.shiftKey ?? false;

      const modifiersMatch =
        ctrlKey === event.ctrlKey &&
        metaKey === event.metaKey &&
        altKey === event.altKey &&
        shiftKey === event.shiftKey;

      const isMultiModifierShortcut = ctrlKey && (metaKey || altKey);
      const altModifiersMatch =
        isMultiModifierShortcut &&
        ((event.ctrlKey && event.metaKey) || (event.ctrlKey && event.altKey));

      if (
        (isKeyMatch || eventKeyIsLetter) &&
        (modifiersMatch || altModifiersMatch)
      ) {
        if (preventDefault) {
          event.preventDefault();
          event.stopPropagation();
        }

        handler(event);
        break;
      }
    }
  }, []);

  useEffect(() => {
    // Use capture phase to intercept events before browser defaults
    document.addEventListener("keydown", handleKeyDown, {
      capture: true,
      passive: false,
    });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
};
